/*
 * Ryan O'Dowd
 * 2024-10-10
 *
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
*/
import useTypedSelector from './useTypedSelector';

const fontFamilies = { // @TODO: add these to material/paper theme as well
  primary: 'Ubuntu-Regular',
  primaryBold: 'Ubuntu-Bold',
  primaryItalic: 'Ubuntu-Italic',
  primaryBoldItalic: 'Ubuntu-BoldItalic',
  secondary: 'Montserrat-Regular',
  secondaryBold: 'Montserrat-ExtraBold',
  secondaryItalic: 'Montserrat-Italic',
  secondaryBoldItalic: 'Montserrat-ExtraBoldItalic',
};

export const accentsGreen = { // @TODO: shouldn't be exported (used in index/index.web.tsx right now)
  accentLight: '#e7ff83',
  accent: '#c6ff00',
  accentDark: '#8ab200',
};

const accentsStorm = {
  accentLight: '#82afff',
  accent: '#007aff',
  accentDark: '#004fcc',
};

const accentsProtanopia = {
  accentLight: '#d9e8ff',
  accent: '#70a8ff',
  accentDark: '#3f6fae',
};

const accentsDeuteranopia = {
  accentLight: '#e1dcff',
  accent: '#9585ff',
  accentDark: '#5c4eae',
};

const accentsTritanopia = {
  accentLight: '#ffe1d2',
  accent: '#ff9566',
  accentDark: '#b95e3d',
};

const accentsAchromatopsia = {
  accentLight: '#eeeeee',
  accent: '#bdbdbd',
  accentDark: '#777777',
};

const test = {
  extraSpelling: '#ff8c64',
  wordsAdded: '#ff02a1',
  spellingPunctuation: '#ffffff',
  wordsMissed: '#ffd770',
  wordsCorrect: '#c6ff00',
};

const testProtanopia = {
  extraSpelling: '#ffb38a',
  wordsAdded: '#f0a6ff',
  spellingPunctuation: '#ffffff',
  wordsMissed: '#ffe39a',
  wordsCorrect: '#b8e4ff',
};

const testDeuteranopia = {
  extraSpelling: '#ffb08a',
  wordsAdded: '#e6b3ff',
  spellingPunctuation: '#ffffff',
  wordsMissed: '#ffe29a',
  wordsCorrect: '#a8dcff',
};

const testTritanopia = {
  extraSpelling: '#b8e5ff',
  wordsAdded: '#d8b8ff',
  spellingPunctuation: '#ffffff',
  wordsMissed: '#fff0b0',
  wordsCorrect: '#a8d8c8',
};

const testAchromatopsia = {
  extraSpelling: '#d0d0d0',
  wordsAdded: '#b8b8b8',
  spellingPunctuation: '#ffffff',
  wordsMissed: '#e4e4e4',
  wordsCorrect: '#a8a8a8',
};

const generalColors = {
  success: '#477158',
  qqq: '#81cff3', // @TODO: rename (used for passage gradient normalization spectrum and i think something else too)
  incorrect: '#FF4241', // @TODO: update
  incorrectText: '#4422ff', // @TODO: update, especially for dark mode (especially storms)
  inContextHightlight: '#ffccaa',
  badge: '#ff4242', // @TODO: '#c6ff00', // @TODO: '#aaaaff', // @TODO: is this what i want for badge color?
  warnIncorrect: '#FFD700', // @TODO: update
  alert: '#FF4242',
  alertLight: '#FF9292',
  alertDark: '#cF2222',
  white: '#ffffff',
  black: '#000000',
  gray: '#757575',
  lightGray: '#cccccc', // @TODO: copy and call unstarted
  extraLightGray: '#eeeeee',
  disabled: '#bdbdbd',
  bgLight: '#EEEEEE',
  bgDark: '#1b1b1b',
  complete: '#FFD700', // @TODO: rename to inProgress, and have accent color assigned to complete
  myNote: '#ccffdd',
  otherNote: '#bbeeff',
  warningBg: '#af4F70',
  // @TODO: highlighted: '#ffde64',
};

const generalColorsProtanopia = {
  success: '#477158',
  qqq: '#81cff3', // @TODO: rename (used for passage gradient normalization spectrum and i think something else too)
  incorrect: '#e05a3f', // @TODO: update
  incorrectText: '#4422ff', // @TODO: update, especially for dark mode (especially storms)
  inContextHightlight: '#ffccaa',
  badge: '#e05a3f', // @TODO: '#c6ff00', // @TODO: '#aaaaff', // @TODO: is this what i want for badge color?
  warnIncorrect: '#e0a500', // @TODO: update
  alert: '#e05a3f',
  alertLight: '#ff9d85',
  alertDark: '#a83a28',
  white: '#ffffff',
  black: '#000000',
  gray: '#757575',
  lightGray: '#cccccc', // @TODO: copy and call unstarted
  extraLightGray: '#eeeeee',
  disabled: '#bdbdbd',
  bgLight: '#EEEEEE',
  bgDark: '#1b1b1b',
  complete: '#e0a500', // @TODO: rename to inProgress, and have accent color assigned to complete
  myNote: '#ccffdd',
  otherNote: '#bbeeff',
  warningBg: '#af4F70',
  // @TODO: highlighted: '#ffde64',
};

const generalColorsDeuteranopia = {
  success: '#477158',
  qqq: '#81cff3', // @TODO: rename (used for passage gradient normalization spectrum and i think something else too)
  incorrect: '#e0643f', // @TODO: update
  incorrectText: '#4422ff', // @TODO: update, especially for dark mode (especially storms)
  inContextHightlight: '#ffccaa',
  badge: '#e0643f', // @TODO: '#c6ff00', // @TODO: '#aaaaff', // @TODO: is this what i want for badge color?
  warnIncorrect: '#d9a000', // @TODO: update
  alert: '#e0643f',
  alertLight: '#ff9f87',
  alertDark: '#aa3b28',
  white: '#ffffff',
  black: '#000000',
  gray: '#757575',
  lightGray: '#cccccc', // @TODO: copy and call unstarted
  extraLightGray: '#eeeeee',
  disabled: '#bdbdbd',
  bgLight: '#EEEEEE',
  bgDark: '#1b1b1b',
  complete: '#d9a000', // @TODO: rename to inProgress, and have accent color assigned to complete
  myNote: '#ccffdd',
  otherNote: '#bbeeff',
  warningBg: '#af4F70',
  // @TODO: highlighted: '#ffde64',
};

const generalColorsTritanopia = {
  success: '#477158',
  qqq: '#81cff3', // @TODO: rename (used for passage gradient normalization spectrum and i think something else too)
  incorrect: '#d94b3d', // @TODO: update
  incorrectText: '#4422ff', // @TODO: update, especially for dark mode (especially storms)
  inContextHightlight: '#ffccaa',
  badge: '#d94b3d', // @TODO: '#c6ff00', // @TODO: '#aaaaff', // @TODO: is this what i want for badge color?
  warnIncorrect: '#9b63b6', // @TODO: update
  alert: '#d94b3d',
  alertLight: '#ff9690',
  alertDark: '#a52e26',
  white: '#ffffff',
  black: '#000000',
  gray: '#757575',
  lightGray: '#cccccc', // @TODO: copy and call unstarted
  extraLightGray: '#eeeeee',
  disabled: '#bdbdbd',
  bgLight: '#EEEEEE',
  bgDark: '#1b1b1b',
  complete: '#9b63b6', // @TODO: rename to inProgress, and have accent color assigned to complete
  myNote: '#ccffdd',
  otherNote: '#bbeeff',
  warningBg: '#af4F70',
  // @TODO: highlighted: '#ffde64',
};

const generalColorsAchromatopsia = {
  success: '#666666',
  qqq: '#b5b5b5', // @TODO: rename (used for passage gradient normalization spectrum and i think something else too)
  incorrect: '#333333', // @TODO: update
  incorrectText: '#555555', // @TODO: update, especially for dark mode (especially storms)
  inContextHightlight: '#dddddd',
  badge: '#333333', // @TODO: '#c6ff00', // @TODO: '#aaaaff', // @TODO: is this what i want for badge color?
  warnIncorrect: '#888888', // @TODO: update
  alert: '#333333',
  alertLight: '#aaaaaa',
  alertDark: '#111111',
  white: '#ffffff',
  black: '#000000',
  gray: '#757575',
  lightGray: '#cccccc', // @TODO: copy and call unstarted
  extraLightGray: '#eeeeee',
  disabled: '#bdbdbd',
  bgLight: '#EEEEEE',
  bgDark: '#1b1b1b',
  complete: '#888888', // @TODO: rename to inProgress, and have accent color assigned to complete
  myNote: '#dddddd',
  otherNote: '#bbbbbb',
  warningBg: '#777777',
  // @TODO: highlighted: '#ffde64',
};

const stormTheme = {
  white: '#ffffdd',
  primaryExtraLight: '#e3e0e6',
  primaryLight: '#4d5973',
  primary: '#3a465c',
  primaryDark: '#273141',
  primaryExtraDark: '#141922',
  ...accentsStorm,
  icons: '#ffffdd', // @TODO: rename to dialogicon
  lightText: '#ffffdd',
  darkText: '#222222',
  dialogText: '#ffffdd',
  completeBg: '#222222', // @TODO: better color?
  popoverBg: '#222222', // @TODO: better color?
  backgroundImage: require('@assets/img/backgrounds/jungle_blue_dark.png'),
  brickTitle: '#ffffdd',
  incorrect: '#0F4241', // @TODO: update
};

// @TODO: dialog backgrounds should be colored (even if just slightly...and maybe gradient?)
export const themes = { // @TODO: update all of these
  blue: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#dfffff',
      primaryLight: '#33a0c5',
      primary: '#0089ab',
      primaryDark: '#005f86',
      primaryExtraDark: '#002d3d',
      icons: '#002d3d',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#002d3d',
      completeBg: '#444444', // @TODO: better color?
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_blue.png'),
      brickTitle: '#b1dff3',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#53c0e5',
      primaryLight: '#0072a3',
      primary: '#004c7a',
      primaryDark: '#00243f',
      primaryExtraDark: '#001a2d',
      icons: '#001a20',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_blue_dark.png'),
      brickTitle: '#87b5c9',
    },
  },
  storm: {
    isHidden: true,
    fontFamilies,
    light: {...stormTheme, dialogText: '#00373a', popoverBg: '#b3c0e6'}, // @TODO: leaky abstraction...shouldn't need to overwrite specific colors here
    dark: stormTheme,
  },
  green: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#d0ffee',
      primaryLight: '#2dbca0',
      primary: '#007e73',
      primaryDark: '#005a52',
      primaryExtraDark: '#00373a',
      icons: '#00373a',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#00373a',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      brickTitle: '#a3f0e2',
      backgroundImage: require('@assets/img/backgrounds/jungle_green.png'),
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#00605e',
      primaryLight: '#005347',
      primary: '#003a32',
      primaryDark: '#00231f',
      primaryExtraDark: '#001713',
      icons: '#001713',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_green_dark.png'),
      brickTitle: '#5d9a90',
    },
  },
  purple: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#e8e5f5',
      primaryLight: '#9b5fc1',
      primary: '#6b2d96',
      primaryDark: '#4b216f',
      primaryExtraDark: '#321b45',
      icons: '#321b45',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#321b45',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_purple.png'),
      brickTitle: '#c3a4df',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#876494',
      primaryLight: '#694285',
      primary: '#250044',
      primaryDark: '#120025',
      primaryExtraDark: '#070012',
      icons: '#05000d',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_purple_dark.png'),
      brickTitle: '#795b88',
    },
  },
  yellow: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#f4f1df',
      primaryLight: '#c5b83f',
      primary: '#a29624',
      primaryDark: '#706817',
      primaryExtraDark: '#39340d',
      icons: '#39340d',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#39340d',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_yellow.png'),
      brickTitle: '#d6cc91',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#71682b',
      primaryLight: '#62591f',
      primary: '#4b4312',
      primaryDark: '#2e290b',
      primaryExtraDark: '#171405',
      icons: '#0f0d03',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_yellow_dark.png'),
      brickTitle: '#a59c65',
    },
  },
  red: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#f5e4e8',
      primaryLight: '#b34a61',
      primary: '#963044',
      primaryDark: '#641d2d',
      primaryExtraDark: '#3d121c',
      icons: '#3d121c',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#3d121c',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_red.png'),
      brickTitle: '#d5a0aa',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#a15b6d',
      primaryLight: '#8d4358',
      primary: '#4b0018',
      primaryDark: '#25000c',
      primaryExtraDark: '#100006',
      icons: '#080004',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_red_dark.png'),
      brickTitle: '#a66f7c',
    },
  },
  grayscale: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#f2f2f2',
      primaryLight: '#cccccc',
      primary: '#999999',
      primaryDark: '#666666',
      primaryExtraDark: '#333333',
      icons: '#333333',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#333333',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale.png'),
      brickTitle: '#b1b1b1',
    },
    dark: {
      white: '#c8c8c8',
      primaryExtraLight: '#a0a0a0',
      primaryLight: '#858585',
      primary: '#242424',
      primaryDark: '#121212',
      primaryExtraDark: '#0c0c0c',
      icons: '#050505',
      lightText: '#787878',
      darkText: '#606060',
      dialogText: '#787878',
      completeBg: '#101010',
      popoverBg: '#191919',
      brickTitle: '#767676',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale_dark.png'),
    },
  },
  protanopia: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#e6e6f2',
      primaryLight: '#4f7fc4',
      primary: '#315f9f',
      primaryDark: '#23436f',
      primaryExtraDark: '#172b47',
      ...accentsProtanopia,
      testProtanopia,
      ...generalColorsProtanopia,
      icons: '#172b47',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#172b47',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale.png'),
      brickTitle: '#9db7dc',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#49658d',
      primaryLight: '#3e5b86',
      primary: '#29466f',
      primaryDark: '#172a47',
      primaryExtraDark: '#0d1828',
      ...accentsProtanopia,
      testProtanopia,
      ...generalColorsProtanopia,
      icons: '#080f19',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale_dark.png'),
      brickTitle: '#7893b8',
    },
  },
  deuteranopia: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#e8e6f2',
      primaryLight: '#596fc4',
      primary: '#384f9e',
      primaryDark: '#28376e',
      primaryExtraDark: '#192448',
      ...accentsDeuteranopia,
      testDeuteranopia,
      ...generalColorsDeuteranopia,
      icons: '#192448',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#192448',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale.png'),
      brickTitle: '#a5b0dc',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#53638d',
      primaryLight: '#455583',
      primary: '#303e6c',
      primaryDark: '#1c2745',
      primaryExtraDark: '#0e1427',
      ...accentsDeuteranopia,
      testDeuteranopia,
      ...generalColorsDeuteranopia,
      icons: '#090d19',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale_dark.png'),
      brickTitle: '#7e8cb7',
    },
  },
  tritanopia: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#fff1df',
      primaryLight: '#c76f32',
      primary: '#a54e1e',
      primaryDark: '#703313',
      primaryExtraDark: '#3b1b0a',
      ...accentsTritanopia,
      testTritanopia,
      ...generalColorsTritanopia,
      icons: '#3b1b0a',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#3b1b0a',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale.png'),
      brickTitle: '#dda98b',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#8a5638',
      primaryLight: '#75462d',
      primary: '#5a301b',
      primaryDark: '#32190e',
      primaryExtraDark: '#190c06',
      ...accentsTritanopia,
      testTritanopia,
      ...generalColorsTritanopia,
      icons: '#0d0603',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale_dark.png'),
      brickTitle: '#ad8069',
    },
  },
  achromatopsia: {
    fontFamilies,
    light: {
      white: '#ffffff',
      primaryExtraLight: '#eeeeee',
      primaryLight: '#999999',
      primary: '#666666',
      primaryDark: '#444444',
      primaryExtraDark: '#222222',
      ...accentsAchromatopsia,
      testAchromatopsia,
      ...generalColorsAchromatopsia,
      icons: '#222222',
      lightText: '#ffffff',
      darkText: '#222222',
      dialogText: '#222222',
      completeBg: '#444444',
      popoverBg: '#ffffff',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale.png'),
      brickTitle: '#aaaaaa',
    },
    dark: {
      white: '#e0e0e0',
      primaryExtraLight: '#777777',
      primaryLight: '#666666',
      primary: '#4a4a4a',
      primaryDark: '#2c2c2c',
      primaryExtraDark: '#151515',
      ...accentsAchromatopsia,
      testAchromatopsia,
      ...generalColorsAchromatopsia,
      icons: '#0a0a0a',
      lightText: '#e0e0e0',
      darkText: '#bbbbbb',
      dialogText: '#e0e0e0',
      completeBg: '#222222',
      popoverBg: '#333333',
      backgroundImage: require('@assets/img/backgrounds/jungle_grayscale_dark.png'),
      brickTitle: '#888888',
    },
  },
};

export default function useTheme() {
  return useTypedSelector((state) => {
    const lightDarkMode = state.settingsLightDarkMode;
    const themeMode = themes[state.theme][lightDarkMode];

    const {backgroundImage, ...themeColors} = themeMode;
    return {
      colors: {
        ...generalColors,
        test,
        themeColor: state.theme,
        ...accentsGreen,
        ...themeColors,
      },
      fonts: themes[state.theme].fontFamilies,
      backgroundImage,
    };
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));
}
